import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTrash,
    faEdit,
    faArrowUp,
    faArrowDown,
    faSync,
    faUsers,
    faFilter,
    faFileExcel,
    faFilePdf,
} from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import baseURL from '../../url';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Register from '../Register/Register';

/* ================== Custom Toast (usa clases globales) ================== */
const CustomToast = ({ toasts }) => {
    if (toasts.length === 0) return null;
    return (
        <div className="product-toast-container">
            {toasts.map(t => (
                <div key={t.id} className={`product-toast ${t.type}`}>
                    <div className="toast-icon">
                        {t.type === 'success' && '✅'}
                        {t.type === 'error' && '❌'}
                        {t.type === 'warning' && '⚠️'}
                        {t.type === 'info' && 'ℹ️'}
                    </div>
                    <div className="toast-content">
                        <div className="toast-title">{t.message}</div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default function UsuariosData() {
    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(false);

    // Filtros
    const [filtroIdUsuario, setFiltroIdUsuario] = useState('');
    const [filtroNombre, setFiltroNombre] = useState('');
    const [filtroRol, setFiltroRol] = useState('');
    const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
    const [filtroFechaHasta, setFiltroFechaHasta] = useState('');
    const [filtroEmail, setFiltroEmail] = useState('');
    const [ordenInvertido, setOrdenInvertido] = useState(false);

    // Toasts
    const [toasts, setToasts] = useState([]);
    const showToast = (message, type = 'info') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3800);
    };

    useEffect(() => {
        cargarUsuarios();
    }, []);

    const cargarUsuarios = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${baseURL}/usuariosGet.php`, { method: 'GET' });
            const data = await res.json();
            setUsuarios(data.usuarios || []);
        } catch (error) {
            console.error('Error al cargar usuarios:', error);
            showToast('Error al cargar usuarios', 'error');
        } finally {
            setLoading(false);
        }
    };

    const recargarUsuarios = () => {
        cargarUsuarios();
        showToast('Datos actualizados', 'info');
    };

    const invertirOrden = () => {
        setUsuarios(prev => [...prev].reverse());
        setOrdenInvertido(o => !o);
        showToast('Orden invertido', 'info');
    };

    const eliminarUsuario = (idUsuario) => {
        Swal.fire({
            title: '¿Eliminar usuario?',
            text: 'Esta acción no se puede deshacer',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await fetch(`${baseURL}/usuariosGet.php?idUsuario=${idUsuario}`, { method: 'DELETE' });
                    const data = await res.json();
                    if (data?.mensaje) {
                        showToast('Usuario eliminado', 'success');
                        cargarUsuarios();
                    } else {
                        showToast(data?.error || 'No se pudo eliminar', 'error');
                    }
                } catch (e) {
                    console.error('Error al eliminar usuario:', e);
                    showToast('Error de conexión al eliminar', 'error');
                }
            }
        });
    };

    const editarUsuario = (idUsuario, rolActual) => {
        Swal.fire({
            title: 'Editar Rol',
            input: 'select',
            inputOptions: { usuario: 'Usuario', admin: 'Admin' },
            inputValue: rolActual,
            showCancelButton: true,
            confirmButtonText: 'Guardar',
            cancelButtonText: 'Cancelar',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await fetch(`${baseURL}/usuariosGet.php?idUsuario=${idUsuario}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ rol: result.value }),
                    });
                    const data = await res.json();
                    if (data?.mensaje) {
                        showToast('Rol actualizado', 'success');
                        cargarUsuarios();
                    } else {
                        showToast(data?.error || 'No se pudo actualizar', 'error');
                    }
                } catch (e) {
                    console.error('Error al editar rol:', e);
                    showToast('Error de conexión al actualizar', 'error');
                }
            }
        });
    };

    const usuariosFiltrados = usuarios.filter(usuario => {
        const idUsuarioMatch = usuario.idUsuario?.toString()?.includes(filtroIdUsuario);
        const nombreMatch = (usuario.nombre || '').toLowerCase().includes(filtroNombre.toLowerCase());
        const rolMatch = !filtroRol || (usuario.rol || '').includes(filtroRol);
        const fechaDesdeMatch = !filtroFechaDesde || new Date(usuario.createdAt) >= new Date(filtroFechaDesde);
        const fechaHastaMatch = !filtroFechaHasta || new Date(usuario.createdAt) <= new Date(filtroFechaHasta);
        const emailMatch = (usuario.email || '').toLowerCase().includes(filtroEmail.toLowerCase());
        return idUsuarioMatch && nombreMatch && rolMatch && fechaDesdeMatch && fechaHastaMatch && emailMatch;
    });

    const descargarExcel = () => {
        const data = usuariosFiltrados.map(u => ({
            'ID Usuario': u.idUsuario,
            Nombre: u.nombre,
            Email: u.email,
            Fecha: u.createdAt,
            Rol: u.rol,
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Usuarios');
        XLSX.writeFile(wb, 'usuarios.xlsx');
        showToast('Excel generado', 'success');
    };

    const descargarPDF = () => {
        const pdf = new jsPDF();
        pdf.text('Lista de Usuarios', 10, 10);
        const columns = ['ID Usuario', 'Nombre', 'Email', 'Rol', 'Fecha'];
        const body = usuariosFiltrados.map(u => [u.idUsuario, u.nombre, u.email, u.rol, u.createdAt]);
        pdf.autoTable({ head: [columns], body });
        pdf.save('usuarios.pdf');
        showToast('PDF generado', 'success');
    };

    return (
        <div>
            <CustomToast toasts={toasts} />

            {/* Header más compacto con stats inline */}
            <div className="global-component-header" style={{ padding: '12px 16px' }}>
                <div className="flex-between">
                    <div className="flex-center" style={{ gap: '16px' }}>
                        <div className="global-component-title">
                            <FontAwesomeIcon icon={faUsers} />
                            Gestión de Usuarios
                        </div>

                        <div className="flex-center" style={{ gap: '12px' }}>
                            <div className="global-stat-card compact">
                                <span className="global-stat-number">{usuarios.length}</span>
                                <span className="global-stat-label">Total</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex-center" style={{ gap: '8px' }}>
                        {/* Cambia los iconos por faFileExcel/faFilePdf si los importas */}
                        <button
                            className="btn-product-primary compact flex-center"
                            style={{ marginLeft: "10px" }}
                            onClick={descargarExcel}
                            title="Exportar Excel"
                        >
                            <FontAwesomeIcon icon={faFileExcel} />
                        </button>
                        <button
                            className="btn-product-primary compact flex-center"
                            onClick={descargarPDF}
                            title="Exportar PDF"
                        >
                            <FontAwesomeIcon icon={faFilePdf} />
                        </button>
                        <button
                            className="btn-product-secondary compact flex-center"
                            onClick={invertirOrden}
                            title="Invertir orden"
                        >
                            <FontAwesomeIcon icon={ordenInvertido ? faArrowUp : faArrowDown} />
                        </button>
                        <button
                            className="btn-product-secondary compact flex-center"
                            onClick={recargarUsuarios}
                            title="Actualizar"
                        >
                            <FontAwesomeIcon icon={faSync} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Filtros en una sola fila más compacta */}
            <div className="product-section-card compact" style={{ margin: '8px 0' }}>
                <div className="product-section-content" style={{ padding: '12px 16px' }}>
                    <div className="flex-between" style={{ gap: '12px', alignItems: 'end' }}>
                        {/* Inputs alineados en una fila — hacen wrap si no caben */}
                        <div className="flex-center" style={{ gap: '12px', flex: 1, flexWrap: 'wrap' }}>
                            <div className="product-form-group compact" style={{ minWidth: 180 }}>
                                <label className="product-form-label">ID Usuario</label>
                                <input
                                    type="number"
                                    className="product-form-input compact"
                                    value={filtroIdUsuario}
                                    onChange={(e) => setFiltroIdUsuario(e.target.value)}
                                    placeholder="ID Usuario"
                                />
                            </div>

                            <div className="product-form-group compact" style={{ minWidth: 200 }}>
                                <label className="product-form-label">Nombre</label>
                                <input
                                    type="text"
                                    className="product-form-input compact"
                                    value={filtroNombre}
                                    onChange={(e) => setFiltroNombre(e.target.value)}
                                    placeholder="Nombre"
                                />
                            </div>

                            <div className="product-form-group compact" style={{ minWidth: 220 }}>
                                <label className="product-form-label">Email</label>
                                <input
                                    type="text"
                                    className="product-form-input compact"
                                    value={filtroEmail}
                                    onChange={(e) => setFiltroEmail(e.target.value)}
                                    placeholder="Email"
                                />
                            </div>

                            <div className="product-form-group compact" style={{ minWidth: 180 }}>
                                <label className="product-form-label">Rol</label>
                                <select
                                    className="product-form-input compact"
                                    value={filtroRol}
                                    onChange={(e) => setFiltroRol(e.target.value)}
                                >
                                    <option value="">Todos</option>
                                    <option value="usuario">Usuario</option>
                                    <option value="admin">Admin</option>
                                </select>
                            </div>

                            <div className="product-form-group compact" style={{ minWidth: 180 }}>
                                <label className="product-form-label">Desde</label>
                                <input
                                    type="date"
                                    className="product-form-input compact"
                                    value={filtroFechaDesde}
                                    onChange={(e) => setFiltroFechaDesde(e.target.value)}
                                />
                            </div>

                            <div className="product-form-group compact" style={{ minWidth: 180 }}>
                                <label className="product-form-label">Hasta</label>
                                <input
                                    type="date"
                                    className="product-form-input compact"
                                    value={filtroFechaHasta}
                                    onChange={(e) => setFiltroFechaHasta(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Botón de orden al extremo derecho */}
                        <button
                            className="btn-product-secondary compact flex-center"
                            onClick={() => setOrdenInvertido((p) => !p)}
                            title="Invertir orden"
                            style={{ minWidth: '90px' }}
                        >
                            <FontAwesomeIcon icon={ordenInvertido ? faArrowUp : faArrowDown} />
                            <span style={{ marginLeft: '6px' }}>Orden</span>
                        </button>
                    </div>
                </div>
            </div>



            {/* Tabla global */}
            <div className="global-table-container">
                {loading ? (
                    <div className="global-loading-state">
                        <FontAwesomeIcon icon={faSync} size="2x" />
                        <div>Cargando usuarios...</div>
                    </div>
                ) : usuariosFiltrados.length > 0 ? (
                    <table className="global-table">
                        <thead>
                            <tr>
                                <th>ID Usuario</th>
                                <th>Nombre</th>
                                <th>Email</th>
                                <th>Rol</th>
                                <th>Fecha Creación</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {usuariosFiltrados.map((usuario, index) => (
                                <tr key={usuario.idUsuario} style={{ animationDelay: `${index * 0.05}s` }}>
                                    <td>{usuario.idUsuario}</td>
                                    <td><strong>{usuario.nombre}</strong></td>
                                    <td>{usuario.email}</td>
                                    <td>
                                        <span
                                            className={
                                                'global-indicator ' +
                                                (usuario.rol === 'admin'
                                                    ? 'success'
                                                    : usuario.rol === 'usuario'
                                                        ? 'warning'
                                                        : 'error')
                                            }
                                        >
                                            {usuario.rol || '—'}
                                        </span>
                                    </td>
                                    <td>{usuario.createdAt}</td>
                                    <td>
                                        <div className="global-table-actions">
                                            <button
                                                className="global-btn-edit"
                                                title="Editar rol"
                                                onClick={() => editarUsuario(usuario.idUsuario, usuario.rol)}
                                            >
                                                <FontAwesomeIcon icon={faEdit} />
                                            </button>
                                            <button
                                                className="global-btn-delete"
                                                title="Eliminar"
                                                onClick={() => eliminarUsuario(usuario.idUsuario)}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="global-empty-state">
                        <FontAwesomeIcon icon={faUsers} size="3x" />
                        <div className="global-empty-title">No hay usuarios</div>
                        <div className="global-empty-description">
                            Ajusta los filtros o crea un nuevo usuario con el formulario superior.
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
